// Workspace card for a driver. Mirrors GateDetailCard's structure (pin /
// minimize / close header) but the body is driver-specific:
//   - status badge with current activity (in run, at gate, on lunch...)
//   - current assignment surface
//   - sector / shift / tug
//   - today's KPIs (runs, bags, hots made/missed)
//   - run history (today)
//   - issues (unaccepted, late, etc)

import React from 'react';
import { Box, Paper, Typography, IconButton, Tooltip, Chip, LinearProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PersonIcon from '@mui/icons-material/Person';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import HistoryIcon from '@mui/icons-material/History';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import LuggageIcon from '@mui/icons-material/Luggage';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import CoffeeIcon from '@mui/icons-material/Coffee';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import PlaceIcon from '@mui/icons-material/Place';
import ChatIcon from '@mui/icons-material/Chat';
import { useIntel } from './IntelligenceContext';
import { DRIVER_DETAILS, DRIVER_STATUS_DISPLAY } from './mockDriverDetails';
import { C } from '../styles';

const SEVERITY_BG = {
  red:  'rgba(255, 0, 0, 0.10)',
  gold: 'rgba(249, 182, 18, 0.10)',
};
const SEVERITY_COLOR = { red: '#FF0000', gold: '#F9B612' };

function resolveDriverDetail(entity) {
  if (!entity || entity.kind !== 'driver') return null;
  return DRIVER_DETAILS.get(entity.id) || null;
}

function DriverDetailCard({ entity, isSelected }) {
  const intel = useIntel();
  const {
    setSelected, clearSelection,
    isPinned, togglePin, toggleMinimize,
  } = intel;

  const detail = resolveDriverDetail(entity);
  if (!detail) return null;
  const pinned = isPinned(entity);
  const statusMeta = DRIVER_STATUS_DISPLAY[detail.status] || DRIVER_STATUS_DISPLAY.on_shift;

  const onClose = () => {
    if (isSelected) clearSelection();
    else togglePin(entity);
  };

  return (
    <Paper
      data-intel-overlay
      elevation={0}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={() => !isSelected && setSelected(entity)}
      sx={{
        backgroundColor: 'rgba(30, 30, 30, 0.7)',
        backdropFilter: C.blur,
        WebkitBackdropFilter: C.blur,
        border: `1px solid ${isSelected ? C.accent : C.border}`,
        borderRadius: 1.5,
        boxShadow: isSelected
          ? `0 0 0 1px ${C.accent}, 0 8px 32px rgba(0, 0, 0, 0.45)`
          : '0 8px 32px rgba(0, 0, 0, 0.30)',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
        flex: '1 1 auto',
        minHeight: 0,
        cursor: isSelected ? 'default' : 'pointer',
      }}
    >
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1,
        px: 1.5, py: 1.25,
        borderBottom: `1px solid ${C.border}`,
        backgroundColor: 'rgba(0,0,0,0.3)',
        flexShrink: 0,
      }}>
        <Box sx={{
          width: 38, height: 38,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: statusMeta.color,
          color: '#fff',
          fontWeight: 700, fontSize: '0.95rem',
          borderRadius: '50%',
          flexShrink: 0,
        }}>
          <PersonIcon sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontSize: '0.55rem', color: C.textMuted, fontWeight: 700,
            letterSpacing: 0.6, textTransform: 'uppercase',
          }}>
            Driver · #{detail.badge} · Seniority {detail.seniority}
          </Typography>
          <Typography noWrap sx={{
            fontSize: '1rem', color: C.textPrimary, fontWeight: 700, lineHeight: 1.15,
          }}>
            {detail.name}
          </Typography>
          <Typography noWrap sx={{ fontSize: '0.65rem', color: C.textSecondary, lineHeight: 1.2 }}>
            {detail.shiftCode} · {detail.sector}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <Tooltip title={pinned ? 'Unpin' : 'Pin to keep open'} placement="bottom">
            <IconButton size="small"
              onClick={(e) => { e.stopPropagation(); togglePin(entity); }}
              sx={{
                color: pinned ? C.accent : C.textMuted,
                '&:hover': { color: C.accent, backgroundColor: 'rgba(249, 182, 18, 0.08)' },
              }}
            >
              {pinned ? <PushPinIcon sx={{ fontSize: 16 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>
          {pinned && (
            <Tooltip title="Minimize" placement="bottom">
              <IconButton size="small"
                onClick={(e) => { e.stopPropagation(); toggleMinimize(entity); }}
                sx={{ color: C.textMuted, '&:hover': { color: C.textSecondary } }}
              >
                <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={isSelected ? 'Clear selection' : 'Close (unpin)'} placement="bottom">
            <IconButton size="small"
              onClick={(e) => { e.stopPropagation(); onClose(); }}
              sx={{ color: C.textMuted, '&:hover': { color: '#f44336' } }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Status banner */}
      <Box sx={{
        px: 1.5, py: 0.75,
        backgroundColor: `${statusMeta.color}15`,
        borderBottom: `1px solid ${statusMeta.color}33`,
        display: 'flex', alignItems: 'center', gap: 0.75,
      }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: statusMeta.color, boxShadow: `0 0 6px ${statusMeta.color}` }} />
        <Typography sx={{
          fontSize: '0.65rem', fontWeight: 800, letterSpacing: 0.6,
          color: statusMeta.color, textTransform: 'uppercase',
        }}>
          {statusMeta.label}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Open thread (not wired yet)">
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.4,
            px: 0.6, py: 0.2,
            borderRadius: 0.4,
            cursor: 'pointer',
            color: C.textMuted,
            '&:hover': { color: C.textSecondary, backgroundColor: 'rgba(255,255,255,0.05)' },
          }}>
            <ChatIcon sx={{ fontSize: 12 }} />
            <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' }}>
              Message
            </Typography>
          </Box>
        </Tooltip>
      </Box>

      {/* Body */}
      <Box sx={{
        flex: 1, overflowY: 'auto', minHeight: 0,
        '&::-webkit-scrollbar': { width: 6 },
        '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(255,255,255,0.12)', borderRadius: 3 },
      }}>
        {/* Issues */}
        {detail.issues.length > 0 && (
          <Box sx={{ px: 1.5, py: 1.25, borderBottom: `1px solid ${C.border}` }}>
            <SectionLabel icon={WarningAmberIcon} color="#FF0000">
              Issues ({detail.issues.length})
            </SectionLabel>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {detail.issues.map((iss, i) => (
                <Box key={i} sx={{
                  display: 'flex', alignItems: 'center', gap: 0.75,
                  px: 0.85, py: 0.5,
                  backgroundColor: SEVERITY_BG[iss.severity],
                  borderLeft: `3px solid ${SEVERITY_COLOR[iss.severity]}`,
                  borderRadius: 0.4,
                }}>
                  <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: SEVERITY_COLOR[iss.severity] }} />
                  <Typography sx={{ fontSize: '0.7rem', color: C.textPrimary, flex: 1 }}>
                    {iss.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Current activity */}
        <Box sx={{ px: 1.5, py: 1.25, borderBottom: `1px solid ${C.border}` }}>
          <SectionLabel icon={DirectionsRunIcon} color={C.textSecondary}>
            Current
          </SectionLabel>
          <CurrentActivityCard current={detail.current} setSelected={setSelected} />
        </Box>

        {/* Tug + Sector */}
        <Box sx={{
          display: 'flex', gap: 1, px: 1.5, py: 1.25,
          borderBottom: `1px solid ${C.border}`,
        }}>
          <MetaTile
            icon={LocalShippingIcon}
            label="Tug"
            value={detail.tug || 'None'}
            color={detail.tug ? C.textPrimary : '#F9B612'}
          />
          <MetaTile
            icon={PlaceIcon}
            label="Sector"
            value={detail.sector.replace('Sector ', '')}
            color={C.textPrimary}
          />
        </Box>

        {/* Today KPIs */}
        <Box sx={{ px: 1.5, py: 1.25, borderBottom: `1px solid ${C.border}` }}>
          <SectionLabel icon={LuggageIcon} color={C.textSecondary}>Today</SectionLabel>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.6 }}>
            <KpiTile value={detail.runsToday} label="Runs" color={C.textPrimary} />
            <KpiTile value={detail.bagsHandled} label="Bags" color={C.textPrimary} />
            <KpiTile value={detail.hotsMade} label="Hots Made" color="#4CAF50" />
            <KpiTile value={detail.hotsMissed} label="Missed" color={detail.hotsMissed > 0 ? '#FF0000' : C.textMuted} />
          </Box>
        </Box>

        {/* History */}
        <Box sx={{ px: 1.5, py: 1.25 }}>
          <SectionLabel icon={HistoryIcon} color={C.textSecondary}>
            Run History ({detail.history.length})
          </SectionLabel>
          {detail.history.length === 0 ? (
            <Typography sx={{ fontSize: '0.7rem', color: C.textMuted, fontStyle: 'italic' }}>
              No runs yet today.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {detail.history.slice().reverse().map(h => (
                <Box key={h.id}
                  onClick={(e) => { e.stopPropagation(); setSelected({ kind: 'flight', id: h.flightNumber }); }}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 0.75,
                    px: 0.85, py: 0.5,
                    backgroundColor: 'rgba(255,255,255,0.025)',
                    border: `1px solid ${C.border}`,
                    borderRadius: 0.4,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
                  }}
                >
                  <Typography sx={{
                    fontSize: '0.55rem', color: C.textMuted, fontWeight: 700,
                    fontVariantNumeric: 'tabular-nums', minWidth: 36,
                  }}>
                    {fmtClock(h.startMin)}
                  </Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                      <Typography sx={{
                        fontSize: '0.72rem', color: C.textPrimary, fontWeight: 700,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      }}>
                        {h.flightNumber.replace(/^WN\s*/, '')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.6rem', color: C.textMuted }}>
                        · {h.gate} · {h.bags}b
                      </Typography>
                    </Box>
                  </Box>
                  {h.hadHot && (
                    <Tooltip title={h.hotMade ? 'Hot bag MADE the connection' : 'Hot bag MISSED the connection'}>
                      <WhatshotIcon sx={{ fontSize: 12, color: h.hotMade ? '#4CAF50' : '#FF0000' }} />
                    </Tooltip>
                  )}
                  <Typography sx={{
                    fontSize: '0.55rem', color: C.textMuted, fontVariantNumeric: 'tabular-nums',
                    minWidth: 24, textAlign: 'right',
                  }}>
                    {h.durationMin}m
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
}

function CurrentActivityCard({ current, setSelected }) {
  if (!current || current.kind === 'idle') {
    return (
      <Typography sx={{ fontSize: '0.75rem', color: C.textMuted, fontStyle: 'italic' }}>
        Idle — available for assignment.
      </Typography>
    );
  }
  if (current.kind === 'in_run') {
    return (
      <Box sx={{
        px: 0.85, py: 0.75,
        backgroundColor: 'rgba(0, 188, 212, 0.08)',
        border: '1px solid rgba(0, 188, 212, 0.3)',
        borderRadius: 0.5,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography sx={{
            fontSize: '0.78rem', color: C.textPrimary, fontWeight: 700,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          }}>
            {current.flightNumber}
          </Typography>
          <Typography sx={{ fontSize: '0.6rem', color: C.textMuted }}>
            · {current.bags} bags
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.4 }}>
          <Chip size="small" label={current.fromGate}
            onClick={(e) => { e.stopPropagation(); setSelected({ kind: 'gate', id: current.fromGate }); }}
            sx={{
              height: 18, fontSize: '0.6rem', fontWeight: 700,
              color: '#94A3B8', backgroundColor: 'rgba(148, 163, 184, 0.12)',
              border: '1px solid rgba(148, 163, 184, 0.3)',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              '& .MuiChip-label': { px: 0.6 },
            }}
          />
          <Typography sx={{ fontSize: '0.6rem', color: '#00BCD4' }}>→</Typography>
          <Chip size="small" label={current.toGate}
            onClick={(e) => { e.stopPropagation(); setSelected({ kind: 'gate', id: current.toGate }); }}
            sx={{
              height: 18, fontSize: '0.6rem', fontWeight: 700,
              color: '#00BCD4', backgroundColor: 'rgba(0, 188, 212, 0.18)',
              border: '1px solid rgba(0, 188, 212, 0.5)',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              '& .MuiChip-label': { px: 0.6 },
            }}
          />
          <Box sx={{ flex: 1 }} />
          <Typography sx={{
            fontSize: '0.62rem', color: '#00BCD4', fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
          }}>
            ETA {current.etaMin}m
          </Typography>
        </Box>
      </Box>
    );
  }
  if (current.kind === 'at_gate') {
    return (
      <Box sx={{
        px: 0.85, py: 0.75,
        backgroundColor: 'rgba(249, 182, 18, 0.08)',
        border: '1px solid rgba(249, 182, 18, 0.3)',
        borderRadius: 0.5,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography sx={{
            fontSize: '0.78rem', color: C.textPrimary, fontWeight: 700,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          }}>
            {current.flightNumber}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mt: 0.4 }}>
          <Chip size="small" label={current.gate}
            onClick={(e) => { e.stopPropagation(); setSelected({ kind: 'gate', id: current.gate }); }}
            sx={{
              height: 18, fontSize: '0.6rem', fontWeight: 700,
              color: '#F9B612', backgroundColor: 'rgba(249, 182, 18, 0.18)',
              border: '1px solid rgba(249, 182, 18, 0.5)',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              '& .MuiChip-label': { px: 0.6 },
            }}
          />
          <Typography sx={{ fontSize: '0.6rem', color: C.textMuted }}>
            Working {current.role}
          </Typography>
        </Box>
      </Box>
    );
  }
  if (current.kind === 'on_lunch' || current.kind === 'on_break') {
    const isLunch = current.kind === 'on_lunch';
    const Icon = isLunch ? RestaurantIcon : CoffeeIcon;
    const color = isLunch ? '#EC4899' : '#9C27B0';
    return (
      <Box sx={{
        px: 0.85, py: 0.6,
        backgroundColor: `${color}15`,
        border: `1px solid ${color}33`,
        borderRadius: 0.5,
        display: 'flex', alignItems: 'center', gap: 0.75,
      }}>
        <Icon sx={{ fontSize: 18, color }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: '0.7rem', color: C.textPrimary, fontWeight: 700, lineHeight: 1.1 }}>
            {isLunch ? 'On lunch' : 'On break'} ({current.durationMin} min)
          </Typography>
          <Typography sx={{ fontSize: '0.6rem', color: C.textMuted }}>
            Started {current.startedAt} · returns {current.returnsAt}
          </Typography>
        </Box>
      </Box>
    );
  }
  return null;
}

function MetaTile({ icon: Icon, label, value, color }) {
  return (
    <Box sx={{
      flex: 1, px: 0.75, py: 0.6,
      backgroundColor: 'rgba(255,255,255,0.025)',
      border: `1px solid ${C.border}`,
      borderRadius: 0.4,
      display: 'flex', alignItems: 'center', gap: 0.6,
    }}>
      <Icon sx={{ fontSize: 14, color: C.textMuted }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{
          fontSize: '0.5rem', color: C.textMuted, fontWeight: 700,
          letterSpacing: 0.5, textTransform: 'uppercase', lineHeight: 1,
        }}>
          {label}
        </Typography>
        <Typography noWrap sx={{
          fontSize: '0.7rem', color, fontWeight: 700,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', lineHeight: 1.1,
        }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

function KpiTile({ value, label, color }) {
  return (
    <Box sx={{
      textAlign: 'center', px: 0.4, py: 0.5,
      backgroundColor: 'rgba(255,255,255,0.02)',
      border: `1px solid ${C.border}`,
      borderRadius: 0.4,
    }}>
      <Typography sx={{
        fontSize: '1.05rem', color, fontWeight: 700,
        fontVariantNumeric: 'tabular-nums', lineHeight: 1.1,
      }}>
        {value}
      </Typography>
      <Typography sx={{
        fontSize: '0.5rem', color: C.textMuted, fontWeight: 700,
        letterSpacing: 0.5, textTransform: 'uppercase', lineHeight: 1,
      }}>
        {label}
      </Typography>
    </Box>
  );
}

function SectionLabel({ icon: Icon, color, children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.6 }}>
      <Icon sx={{ fontSize: 12, color }} />
      <Typography sx={{
        fontSize: '0.55rem', color: C.textMuted, fontWeight: 700,
        letterSpacing: 0.7, textTransform: 'uppercase',
      }}>{children}</Typography>
    </Box>
  );
}

function fmtClock(mins) {
  const h = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export default DriverDetailCard;
export { resolveDriverDetail };
